import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';

export default function SpecialDays() {
  const { user } = useAuth();
  const isClient = user?.role === 'client';

  const [searchParams] = useSearchParams();
  const [month, setMonth]       = useState(() => {
    const qm = searchParams.get('month');
    if (qm && /^\d{4}-\d{2}$/.test(qm)) return qm;
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });
  const [records, setRecords]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(null);  // record id
  const [selected, setSelected] = useState([]);    // for client self-service
  const [note, setNote]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg]           = useState(null);

  useEffect(() => { load(); }, [month]);

  const load = async () => {
    setLoading(true);
    try {
      const url = isClient ? '/my-day-selections' : `/day-selections?month=${month}`;
      const res = await api.get(url);
      let data = res.data?.data || [];
      // For clients, filter by month
      if (isClient) data = data.filter(r => r.month === month);
      setRecords(data);
    } catch {} finally { setLoading(false); }
  };

  const toggleDay = (recId, day) => {
    const rec = records.find(r => r.id === recId);
    if (!rec || rec.status === 'submitted' || rec.status === 'agency_filled') return;
    const exists = selected.find(s => s.date === day.date);
    if (exists) {
      setSelected(selected.filter(s => s.date !== day.date));
    } else {
      if (selected.length >= rec.allowance) return;
      setSelected([...selected, day]);
    }
  };

  const submitSelection = async (rec) => {
    if (selected.length === 0) return;
    setSubmitting(true);
    try {
      await api.post(`/day-select/${rec.token}`, { selections: selected, client_note: note || null });
      setMsg({ ok: true, text: 'Your selections have been submitted successfully!' });
      setSelected([]); setNote('');
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.message || 'Failed to submit selections.' });
    } finally { setSubmitting(false); }
  };

  const statusBadge = (s) => {
    const map = {
      pending:      { label:'Pending',      bg:'#f59e0b18', color:'#f59e0b', border:'#f59e0b44' },
      submitted:    { label:'Submitted',    bg:'#10b98118', color:'#10b981', border:'#10b98144' },
      agency_filled:{ label:'Agency Filled', bg:'#6c58f018', color:'#6c58f0', border:'#6c58f044' },
    };
    const st = map[s] || map.pending;
    return <span style={{ padding:'3px 10px', borderRadius:20, fontSize:'0.72rem', fontWeight:600, background:st.bg, color:st.color, border:`1px solid ${st.border}` }}>{st.label}</span>;
  };

  const catBadge = (cat) => {
    const colors = {
      'Public Holiday': '#ef4444', 'Festival': '#f59e0b', 'Awareness Day': '#3b82f6',
      'Social Media Day': '#8b5cf6', 'Fun Day': '#ec4899', 'International Day': '#06b6d4',
    };
    const c = colors[cat] || '#6b7280';
    return <span style={{ padding:'2px 8px', borderRadius:12, fontSize:'0.68rem', fontWeight:600, background:c+'18', color:c, border:`1px solid ${c}33` }}>{cat}</span>;
  };

  const monthLabel = (m) => {
    const [y, mo] = m.split('-');
    return new Date(y, mo-1).toLocaleDateString('en-US', { month:'long', year:'numeric' });
  };

  // Month navigation
  const changeMonth = (dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
  };

  return (
    <div className="page-wrap">
      {/* Month selector */}
      <div className="card" style={{ marginBottom:16 }}>
        <div className="card-header" style={{ flexWrap:'wrap', gap:12 }}>
          <div style={{ flex:1 }}>
            <div className="card-title">🗓️ Special Days</div>
            <div className="text-sm text-muted">
              {isClient ? 'View and select your special days for content planning.' : 'View all client special day selections and their status.'}
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <button className="btn btn-secondary" style={{ padding:'6px 10px', fontSize:'0.8rem' }} onClick={() => changeMonth(-1)}>← Prev</button>
            <span className="fw-600" style={{ minWidth:140, textAlign:'center' }}>{monthLabel(month)}</span>
            <button className="btn btn-secondary" style={{ padding:'6px 10px', fontSize:'0.8rem' }} onClick={() => changeMonth(1)}>Next →</button>
          </div>
        </div>
      </div>

      {msg && (
        <div style={{
          padding:'10px 14px', borderRadius:8, fontSize:'0.82rem', marginBottom:14,
          background: msg.ok ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${msg.ok ? '#22c55e' : '#ef4444'}`,
          color: msg.ok ? '#16a34a' : '#b91c1c',
        }}>{msg.text}</div>
      )}

      {loading ? (
        <div className="text-muted" style={{ textAlign:'center', padding:40 }}>Loading...</div>
      ) : records.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:40 }}>
          <div style={{ fontSize:'2rem', marginBottom:8 }}>📭</div>
          <div className="text-muted">No special day requests for {monthLabel(month)}.</div>
          {!isClient && <div className="text-xs text-muted" style={{ marginTop:4 }}>Send requests from the Monthly Planner page.</div>}
        </div>
      ) : isClient ? (
        /* ── CLIENT VIEW ── */
        records.map(rec => (
          <div key={rec.id} className="card" style={{ marginBottom:16 }}>
            <div className="card-header">
              <div>
                <div className="card-title" style={{ fontSize:'0.9rem' }}>
                  {monthLabel(rec.month)} — Allowance: {rec.allowance} days
                </div>
                <div className="text-xs text-muted">
                  {rec.status === 'pending' ? 'Select your preferred special days below.' : `Submitted${rec.submitted_at ? ' on ' + new Date(rec.submitted_at).toLocaleDateString() : ''}`}
                </div>
              </div>
              {statusBadge(rec.status)}
            </div>

            {/* Available days grid */}
            <div style={{ padding:16 }}>
              <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                {(rec.days || []).map(day => {
                  const isSel = rec.status === 'pending'
                    ? selected.find(s => s.date === day.date)
                    : (rec.selections || []).find(s => s.date === day.date);
                  return (
                    <div
                      key={day.date}
                      onClick={() => rec.status === 'pending' && toggleDay(rec.id, day)}
                      style={{
                        padding:'10px 14px', borderRadius:10, minWidth:160,
                        border: isSel ? '2px solid #4f46e5' : '2px solid var(--border)',
                        background: isSel ? '#4f46e518' : 'var(--bg)',
                        cursor: rec.status === 'pending' ? 'pointer' : 'default',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                        {rec.status === 'pending' && (
                          <span style={{ width:18, height:18, borderRadius:4, border:'2px solid '+(isSel?'#4f46e5':'var(--border)'), background:isSel?'#4f46e5':'transparent', display:'inline-flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'0.7rem', fontWeight:700 }}>
                            {isSel ? '✓' : ''}
                          </span>
                        )}
                        <span className="fw-600 text-sm">{day.name}</span>
                      </div>
                      <div className="text-xs text-muted">{day.date}</div>
                      {day.cat && <div style={{ marginTop:4 }}>{catBadge(day.cat)}</div>}
                    </div>
                  );
                })}
              </div>

              {rec.status === 'pending' && (
                <div style={{ marginTop:16 }}>
                  <div className="text-xs text-muted" style={{ marginBottom:6 }}>
                    Selected: {selected.length} / {rec.allowance}
                  </div>
                  <textarea
                    placeholder="Add a note (optional)..."
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    style={{ width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid var(--border)', background:'var(--bg)', color:'var(--text)', fontSize:'0.82rem', resize:'vertical', minHeight:60 }}
                  />
                  <button
                    className="btn btn-primary"
                    style={{ marginTop:8 }}
                    disabled={selected.length === 0 || submitting}
                    onClick={() => submitSelection(rec)}
                  >
                    {submitting ? 'Submitting...' : `Submit ${selected.length} Selection${selected.length !== 1 ? 's' : ''}`}
                  </button>
                </div>
              )}

              {rec.status !== 'pending' && rec.client_note && (
                <div style={{ marginTop:12, padding:'8px 12px', borderRadius:8, background:'var(--bg-alt,#f8fafc)', border:'1px solid var(--border)', fontSize:'0.82rem' }}>
                  <span className="fw-600">Note:</span> {rec.client_note}
                </div>
              )}
            </div>
          </div>
        ))
      ) : (
        /* ── AGENCY VIEW ── */
        <div className="card">
          <div style={{ overflow:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
              <thead>
                <tr style={{ background:'var(--bg-alt,#f8fafc)', borderBottom:'1px solid var(--border)' }}>
                  <th style={thStyle}>Client</th>
                  <th style={thStyle}>Allowance</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Selected Days</th>
                  <th style={thStyle}>Submitted</th>
                  <th style={{...thStyle, width:80, textAlign:'center'}}>Details</th>
                </tr>
              </thead>
              <tbody>
                {records.map(rec => {
                  const client = rec.client || {};
                  const selCount = (rec.selections || []).length;
                  const isExp = expanded === rec.id;
                  return (
                    <React.Fragment key={rec.id}>
                      <tr style={{ borderBottom:'1px solid var(--border)' }}>
                        <td style={tdStyle}>
                          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            <div style={{ width:30, height:30, borderRadius:'50%', background:'#3b82f6', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:'0.68rem', flexShrink:0 }}>
                              {(client.name||'').slice(0,2).toUpperCase()}
                            </div>
                            <div>
                              <div className="fw-600">{client.name || 'Unknown'}</div>
                              <div className="text-xs text-muted">{client.email || ''}</div>
                            </div>
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <span className="fw-600">{rec.allowance}</span> <span className="text-muted">days</span>
                        </td>
                        <td style={tdStyle}>{statusBadge(rec.status)}</td>
                        <td style={tdStyle}>
                          {rec.status === 'pending' ? (
                            <span className="text-muted">Awaiting response</span>
                          ) : (
                            <span className="fw-600">{selCount} / {rec.allowance}</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <span className="text-sm">
                            {rec.submitted_at ? new Date(rec.submitted_at).toLocaleDateString() : '—'}
                          </span>
                        </td>
                        <td style={{...tdStyle, textAlign:'center'}}>
                          <button
                            className="btn btn-secondary"
                            style={{ fontSize:'0.7rem', padding:'3px 10px' }}
                            onClick={() => setExpanded(isExp ? null : rec.id)}
                          >
                            {isExp ? '▲ Hide' : '▼ View'}
                          </button>
                        </td>
                      </tr>
                      {isExp && (
                        <tr>
                          <td colSpan={6} style={{ padding:'0 14px 14px', background:'var(--bg-alt,#f8fafc)' }}>
                            <div style={{ padding:12 }}>
                              <div className="fw-600 text-sm" style={{ marginBottom:8 }}>Available Days ({(rec.days||[]).length})</div>
                              <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:12 }}>
                                {(rec.days || []).map(day => {
                                  const isSel = (rec.selections || []).find(s => s.date === day.date);
                                  return (
                                    <div key={day.date} style={{
                                      padding:'6px 12px', borderRadius:8,
                                      border: isSel ? '2px solid #4f46e5' : '1px solid var(--border)',
                                      background: isSel ? '#4f46e518' : 'var(--bg)',
                                      fontSize:'0.8rem',
                                    }}>
                                      <span className="fw-600">{day.name}</span>
                                      <span className="text-muted" style={{ marginLeft:6, fontSize:'0.72rem' }}>{day.date}</span>
                                      {day.cat && <span style={{ marginLeft:6 }}>{catBadge(day.cat)}</span>}
                                      {isSel && <span style={{ marginLeft:6, color:'#4f46e5', fontWeight:700 }}>✓</span>}
                                    </div>
                                  );
                                })}
                              </div>
                              {rec.client_note && (
                                <div style={{ padding:'8px 12px', borderRadius:8, background:'var(--bg)', border:'1px solid var(--border)', fontSize:'0.82rem' }}>
                                  <span className="fw-600">Client Note:</span> {rec.client_note}
                                </div>
                              )}
                              {rec.sent_via && (
                                <div className="text-xs text-muted" style={{ marginTop:6 }}>Sent via {rec.sent_via}</div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  textAlign:'left', padding:'10px 14px', fontSize:'0.75rem',
  fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase',
  letterSpacing:'0.04em',
};
const tdStyle = {
  padding:'10px 14px', verticalAlign:'middle',
};
